/**
 * FILE: auth-tracking.service.ts
 * CHỨC NĂNG: Kiểm tra quyền truy cập chạy ngầm qua POST /auth/tracking khi đang ở màn hội thoại.
 * TÁC DỤNG: Mở app không gọi lại joinpages khi phiên BE còn nguyên (PageSessionService), nên các kiểm tra của
 *           joinpages được chạy định kỳ ở đây: tài khoản / nhân viên bị khoá hoặc xoá, giới hạn giờ vào doanh nghiệp,
 *           page bị gỡ khỏi tài khoản, page hết hạn. Chỉ chạy ở /conversation (nơi nhân viên trực chat) khi đã đăng nhập
 *           + chọn page — không chạy cho khách, trang công khai, luồng đăng nhập / chọn page, cài đặt...
 *           Không chặn điều hướng; AppComponent xử lý kết quả.
 *
 * FE13 parity: app.component.ts trackingAuth() (/auth/trackingauth mỗi 2 phút)
 */
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {DestroyRef, inject, Injectable, PLATFORM_ID} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {fromEvent, merge, Observable, of, Subject, timer} from 'rxjs';
import {catchError, exhaustMap, filter, map} from 'rxjs/operators';
import {LoginApiService} from '@shared/services/api/login-page/login-api.service';
import {AuthService} from './auth.service';

// ─── Constants ────────────────────────────────────────────────────────────────

/** data.action của /auth/tracking (khớp AuthApiService::TRACKING_ACTION_* ở BE) */
export const AUTH_TRACKING_ACTION = {
  OK: 1,
  /** BE đã huỷ phiên → đăng xuất */
  LOGOUT: 2,
  /** BE đã thoát page → về màn chọn page, vẫn giữ đăng nhập */
  OUT_PAGES: 3,
  /** Phiên BE không còn page trong khi FE vẫn đang dùng → khôi phục ngầm bằng joinpages */
  RESTORE_PAGES: 4,
} as const;
export type AuthTrackingActionValue = typeof AUTH_TRACKING_ACTION[keyof typeof AUTH_TRACKING_ACTION];

/** Lần kiểm tra đầu sau khi mở app (chờ các request khởi động chạy xong) */
const FIRST_CHECK_DELAY = 3_000;

/** Khoảng cách tối thiểu giữa 2 lần gọi API */
const CHECK_INTERVAL = 5 * 60_000;

/** Nhịp xét điều kiện (không gọi API nếu chưa tới hạn) */
const CHECK_TICK = 60_000;

/** Chỉ kiểm tra ở màn hội thoại (web và shell app cùng route /conversation) — trang mới thêm sau không bị ảnh hưởng */
const TRACKING_ROUTE_SEGMENT = 'conversation';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IAuthTrackingResult {
  action: AuthTrackingActionValue;
  message: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({providedIn: 'root'})
export class AuthTrackingService {
  private readonly loginApi = inject(LoginApiService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _result$ = new Subject<IAuthTrackingResult>();
  /** Kết quả cần xử lý (đăng xuất / thoát page / khôi phục page); kết quả bình thường không phát */
  readonly result$ = this._result$.asObservable();

  private isRunning = false;
  private lastCheckedAt = 0;

  /** Bắt đầu kiểm tra định kỳ. Idempotent — gọi nhiều lần không sao. */
  start(): void {
    if (!this.isBrowser || this.isRunning) return;
    this.isRunning = true;

    merge(
      timer(FIRST_CHECK_DELAY, CHECK_TICK),
      // Vừa chuyển vào màn hội thoại (VD: mở app ở trang cài đặt rồi mới sang) → kiểm tra ngay nếu đã tới hạn
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
      // Quay lại tab / mở app từ nền → kiểm tra ngay nếu đã tới hạn
      fromEvent(this.document, 'visibilitychange'),
    ).pipe(
      filter(() => this.isDue()),
      exhaustMap(() => this.check()),
      filter((result): result is IAuthTrackingResult => !!result),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => this._result$.next(result));
  }

  /**
   * Gọi /auth/tracking một lần.
   * null = bình thường hoặc không xác định (lỗi mạng, BE chưa đọc được phiên) → không làm gì, tránh đăng xuất nhầm.
   */
  check(): Observable<IAuthTrackingResult | null> {
    this.lastCheckedAt = Date.now();
    const token = this.authService.getAuthorizationToken();

    return this.loginApi.authTracking().pipe(
      map(response => {
        // Đã đăng xuất / đổi tài khoản trong lúc chờ → bỏ kết quả cũ
        if (!token || token !== this.authService.getAuthorizationToken() || Number(response?.code) !== 1) return null;

        const action = Number(response?.data?.action);
        // BE đã huỷ phiên → luôn đăng xuất theo, kể cả vừa rời màn hội thoại (lần gọi sau BE chỉ trả code 0)
        if (action === AUTH_TRACKING_ACTION.LOGOUT) {
          return {action, message: String(response?.data?.message ?? '')};
        }
        // Thoát / khôi phục page chỉ khi vẫn ở màn hội thoại và còn dùng page (VD: vừa sang luồng kết nối kênh thì
        // bỏ qua, quay lại hội thoại lần sau BE báo RESTORE_PAGES → joinpages kiểm tra lại)
        if ((action === AUTH_TRACKING_ACTION.OUT_PAGES || action === AUTH_TRACKING_ACTION.RESTORE_PAGES)
          && this.isTrackingRoute() && this.authService.hasJoinPages()) {
          return {action, message: String(response?.data?.message ?? '')};
        }
        return null;
      }),
      catchError(() => of(null)),
    );
  }

  /** Tab đang hiển thị, đang ở màn hội thoại, đã đăng nhập + chọn page và đã đủ CHECK_INTERVAL từ lần gọi trước */
  private isDue(): boolean {
    return this.document.visibilityState !== 'hidden'
      && this.isTrackingRoute()
      && this.authService.isLoginEd()
      && this.authService.hasJoinPages()
      && Date.now() - this.lastCheckedAt >= CHECK_INTERVAL;
  }

  private isTrackingRoute(): boolean {
    return this.router.url.split(/[?#]/)[0].split('/')[1] === TRACKING_ROUTE_SEGMENT;
  }
}
